import React, { useState, useEffect, useRef } from 'react';
import { API_URL, getImageUrl } from '../../utils/api';
import LearningStatusBadge from '../ui/LearningStatusBadge';
import { LoadingSpinner } from '../ui/Icons';
import Confetti from 'react-confetti';
import ImageCarousel from '../common/ImageCarousel';
import { GraduationCap, Users } from 'lucide-react';

import QuizLayout from './shared/QuizLayout';
import QuizHeader from './shared/QuizHeader';

const FamilyQuiz = ({ deck, onFinish, onReview, trackInteraction, userPlantData, onAbort, plants, theme }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [options, setOptions] = useState([]);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [results, setResults] = useState([]);
    const [score, setScore] = useState(0);
    const startTimeRef = useRef(Date.now());

    const currentCard = deck[currentIndex];
    const userCardData = userPlantData?.[currentCard?.id] || {};
    const currentStatus = userCardData.learning_status || 'NOT_LEARNED';

    useEffect(() => {
        startTimeRef.current = Date.now();
    }, [currentIndex]);

    useEffect(() => {
        if (!currentCard || !plants) return;
        const families = [...new Set(plants.filter(p => p.family).map(p => p.family))];
        const correctFamily = currentCard.family;
        const wrongFamilies = families.filter(f => f !== correctFamily).sort(() => 0.5 - Math.random()).slice(0, 3);
        const choices = [correctFamily, ...wrongFamilies].sort(() => 0.5 - Math.random());
        setOptions(choices);
        setSelectedAnswer(null);
    }, [currentIndex, currentCard, plants]);

    const handleAnswer = (family) => {
        if (selectedAnswer) return;
        const timeTaken = (Date.now() - startTimeRef.current) / 1000;
        const correct = family === currentCard.family;
        setSelectedAnswer(family);
        const result = { plant: currentCard, correct, userAnswer: family };
        const updatedResults = [...results, result];
        setResults(updatedResults);

        if (correct) setScore(p => p + 1);
        if (onReview) onReview(currentCard, correct, null, timeTaken);

        if (trackInteraction) {
            trackInteraction(correct ? 'FLASHCARD_CORRECT' : 'FLASHCARD_INCORRECT', {
                plant_id: currentCard.id,
                time_taken: timeTaken,
                mode: 'family'
            });
        }

        setTimeout(() => {
            if (currentIndex < deck.length - 1) setCurrentIndex(p => p + 1);
            else onFinish(updatedResults);
        }, correct ? 1500 : 2500);
    };

    if (!currentCard) return <LoadingSpinner />;

    return (
        <QuizLayout onClose={onAbort} theme={theme} className="flex flex-col overflow-hidden">
            {selectedAnswer && selectedAnswer === currentCard.family && <Confetti numberOfPieces={100} recycle={false} gravity={0.3} style={{ zIndex: 100 }} />}

            {/* Premium Header */}
            <QuizHeader
                current={currentIndex + 1}
                total={deck.length}
                onClose={onAbort}
                status={currentStatus}
                theme={theme}
            />

            {/* Workspace */}
            <div className="flex-1 flex flex-col min-h-0 space-y-3 md:space-y-4 px-4 md:px-12 pb-4 md:pb-8">
                {/* Image Container with Elegant Shadow */}
                <div className="flex-1 min-h-0 relative w-full aspect-[21/9] md:aspect-[21/9] bg-slate-900 rounded-[2rem] overflow-hidden shadow-[0_32px_64px_-12px_rgba(0,0,0,0.6)] border border-white/10 group">
                    <ImageCarousel
                        images={currentCard.images}
                        alt={currentCard.german_name}
                        className="h-full w-full object-cover transition-transform duration-1000 group-hover:scale-110 opacity-80"
                        showThumbnails={false}
                    />

                    {/* Score Shield */}
                    <div className="absolute top-3 right-3 z-40">
                        <div className="flex items-center gap-2 px-4 py-2 bg-purple-500/90 backdrop-blur-xl border border-purple-400/50 rounded-xl shadow-lg text-white">
                            <Users className="w-4 h-4" />
                            <div className="flex flex-col">
                                <span className="text-[8px] font-black uppercase tracking-[0.2em] opacity-70 leading-none">Punkte</span>
                                <span className="text-xs font-black tracking-tighter tabular-nums leading-none">{score}</span>
                            </div>
                        </div>
                    </div>

                    {/* Top Overlay - Exam Badge */}
                    {currentCard.is_intermediate_exam_subject && (
                        <div className="absolute top-3 left-3 z-40">
                            <div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-400/95 backdrop-blur-xl border border-yellow-200/50 rounded-xl shadow-lg">
                                <GraduationCap className="w-4 h-4 text-yellow-950" />
                                <span className="text-[9px] md:text-[10px] font-black uppercase text-yellow-950 tracking-widest">Wichtig</span>
                            </div>
                        </div>
                    )}

                    {/* Question Overlay - Sophisticated Glassmorphism */}
                    <div className="absolute bottom-3 inset-x-3 p-3 md:p-6 bg-white/5 backdrop-blur-3xl border border-white/20 rounded-[1.5rem] shadow-2xl text-center z-30">
                        <span className="text-[8px] uppercase tracking-[0.5em] font-black text-purple-400 mb-1 block drop-shadow-md">Taxonomie-Quiz</span>
                        <h3 className="text-xl md:text-3xl font-black text-white leading-tight tracking-tight italic drop-shadow-2xl">
                            {currentCard.botanical_name}
                        </h3>
                    </div>
                </div>

                {/* Options Area */}
                <div className="flex-shrink-0 grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                    {options.map((family, idx) => {
                        const isDark = theme === 'dark';
                        let optionClass = isDark
                            ? "bg-white/5 backdrop-blur-xl border border-white/10 hover:border-purple-500/50 hover:bg-white/15 text-white shadow-xl"
                            : "bg-white/60 backdrop-blur-xl border border-slate-200 hover:border-purple-500/50 hover:bg-white text-slate-900 shadow-md";

                        if (selectedAnswer) {
                            if (family === currentCard.family) {
                                optionClass = "bg-purple-600 border-purple-400 text-white shadow-[0_0_40px_rgba(168,85,247,0.5)] ring-4 ring-purple-600/30 z-10 scale-[1.02]";
                            } else if (family === selectedAnswer) {
                                optionClass = "bg-red-500 border-red-400 text-white shadow-[0_0_30px_rgba(239,68,68,0.4)] opacity-95";
                            } else {
                                optionClass = isDark
                                    ? "bg-slate-900/40 border-transparent text-white/20 opacity-40 scale-[0.98]"
                                    : "bg-slate-100/40 border-transparent text-slate-300 opacity-40 scale-[0.98]";
                            }
                        }

                        return (
                            <button
                                key={idx}
                                onClick={() => handleAnswer(family)}
                                disabled={!!selectedAnswer}
                                className={`
                                    relative px-6 py-4 md:py-6 rounded-[1.8rem] text-center transition-all duration-300 active:scale-95 flex items-center justify-center group font-black text-lg md:text-2xl tracking-tight overflow-hidden
                                    ${optionClass}
                                `}
                            >
                                <span className={selectedAnswer ? 'drop-shadow-lg' : ''}>
                                    {family || 'Unbekannt'}
                                </span>
                                
                                {selectedAnswer && family === currentCard.family && (
                                    <div className="absolute right-6 w-8 h-8 md:w-10 md:h-10 rounded-full bg-white text-purple-600 flex items-center justify-center shadow-2xl animate-in zoom-in duration-300">
                                        <span className="text-lg md:text-xl font-black">✓</span>
                                    </div>
                                )}
                                {selectedAnswer && family === selectedAnswer && family !== currentCard.family && (
                                    <div className="absolute right-6 w-8 h-8 md:w-10 md:h-10 rounded-full bg-white text-red-600 flex items-center justify-center shadow-2xl animate-in shake duration-300">
                                        <span className="text-lg md:text-xl font-black">✗</span>
                                    </div>
                                )}
                                
                                <div className={`absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none ${selectedAnswer ? 'hidden' : ''}`} />
                            </button>
                        );
                    })}
                </div>
            </div>
        </QuizLayout>
    );
};

export default FamilyQuiz;
